import {Children, Vnode} from 'mithril';
import app from 'flarum/forum/app';
import Page from 'flarum/common/components/Page';
import Button from 'flarum/common/components/Button';
import LinkButton from 'flarum/common/components/LinkButton';
import LoadingIndicator from 'flarum/common/components/LoadingIndicator';
import Placeholder from 'flarum/common/components/Placeholder';
import listItems from 'flarum/common/helpers/listItems';
import icon from 'flarum/common/helpers/icon';
import NewFormModal from '../modals/NewFormModal';
import PageContainer from '../components/PageContainer';
import FormListState from '../states/FormListState';
import FormSortDropdown from '../components/FormSortDropdown';
import submissionCount from '../utils/submissionCount';
import GlobalSettings from '../components/GlobalSettings';
import {formulaireFormEdit, formulaireFormShow, formulaireSubmissionIndex} from '../routes';
import SearchInput from '../components/SearchInput';

const translationPrefix = 'kilowhat-formulaire.forum.form-index.';

const TABS = [
    {
        key: 'standalone',
        icon: 'fas fa-poll-h',
        route: 'formulaireFormIndex',
    },
    {
        key: 'discussion',
        icon: 'fas fa-comment-alt',
        route: 'formulaireFormIndexDiscussion',
    },
    {
        key: 'user',
        icon: 'fas fa-user-tag',
        route: 'formulaireFormIndexUser',
    },
];

interface FormIndexPageAttrs {
    routeName: string
}

export default class FormIndexPage extends Page {
    formType!: 'discussion' | 'user' | 'standalone'
    formList!: FormListState
    attrs!: FormIndexPageAttrs

    oninit(vnode: Vnode<FormIndexPageAttrs, this>) {
        super.oninit(vnode);

        switch (this.attrs.routeName) {
            case 'formulaireFormIndexDiscussion':
                this.formType = 'discussion';
                break;
            case 'formulaireFormIndexUser':
                this.formType = 'user';
                break;
            default:
                this.formType = 'standalone';
        }

        this.formList = new FormListState({
            q: 'type:' + this.formType,
        });
        this.formList.refresh();
    }

    view(): Children {
        return m(PageContainer, [
            m('.FormulaireHeader.FormulaireHeader--banner', [
                icon('fas fa-poll-h', {
                    className: 'FormulaireBanner',
                }),
                m('h1', app.translator.trans(translationPrefix + 'header.' + 'title')),
                m('ul.FormulaireHeaderLinks', [
                    m('li', m('a', {
                        target: '_blank',
                        href: 'https://kilowhat.net/flarum/extensions/formulaire',
                    }, [
                        app.translator.trans(translationPrefix + 'header.' + 'documentation'),
                        ' ',
                        icon('fas fa-external-link-alt'),
                    ])),
                    m('li', m('a', {
                        target: '_blank',
                        href: 'https://discuss.flarum.org/d/23063',
                    }, [
                        app.translator.trans(translationPrefix + 'header.' + 'discuss'),
                        ' ',
                        icon('fas fa-external-link-alt'),
                    ])),
                ]),
                m('.FormulaireTabs', TABS.map(tab => LinkButton.component({
                    className: this.formType === tab.key ? 'active' : '',
                    icon: tab.icon,
                    href: app.route(tab.route),
                }, app.translator.trans(translationPrefix + 'tabs.' + tab.key)))),
            ]),
            m(GlobalSettings, {
                type: this.formType,
            }),
            m('.FormulaireFilters', [
                m(FormSortDropdown, {
                    sort: this.formList.params.sort,
                    onchange: (value: string) => {
                        this.formList.params.sort = value;
                        this.formList.refresh();
                    },
                }),
                m(SearchInput, {
                    initialValue: '',
                    onchange: (value: string) => {
                        this.formList.params.q = 'type:' + this.formType + ' ' + value;
                        this.formList.refresh();
                    },
                    placeholder: app.translator.trans(translationPrefix + 'search'),
                }),
                Button.component({
                    className: 'Button Button--primary FormulaireFilters--right',
                    icon: 'fas fa-plus',
                    onclick: () => {
                        app.modal.show(NewFormModal, {
                            linkType: this.formType === 'discussion' ? 'tags' : (this.formType === 'user' ? 'groups' : null),
                        });
                    },
                }, app.translator.trans(translationPrefix + 'new')),
            ]),
            this.list(this.formList),
        ]);
    }

    list(state: FormListState) {
        let loading;

        if (state.loading) {
            loading = LoadingIndicator.component();
        } else if (state.moreResults) {
            loading = Button.component({
                className: 'Button',
                onclick: state.loadMore.bind(state),
            }, app.translator.trans(translationPrefix + 'load-more'));
        }

        if (state.forms.length === 0 && !state.loading) {
            return Placeholder.component({
                text: app.translator.trans(translationPrefix + 'empty'),
            });
        }

        return m('div', [
            m('table.FormulaireDataTable', [
                m('thead', m('tr', [
                    m('th', app.translator.trans(translationPrefix + 'columns.title')),
                    this.formType === 'standalone' ? [
                        m('th', app.translator.trans(translationPrefix + 'columns.accept-submissions')),
                        m('th', app.translator.trans(translationPrefix + 'columns.allow-modification')),
                    ] : [
                        m('th', app.translator.trans(translationPrefix + 'columns.enabled')),
                    ],
                    m('th', app.translator.trans(translationPrefix + 'columns.submissions')),
                    m('th', app.translator.trans(translationPrefix + 'columns.actions')),
                ])),
                m('tbody', state.forms.map(form => {
                    const badges = form.badges().toArray();

                    return m('tr', [
                        m('td', [
                            form.private_title(),
                            badges.length ? m('ul.FormulaireDataTable-badges.badges', listItems(badges)) : null,
                        ]),
                        m('td', form.accept_submissions() ? icon('fas fa-check') : null),
                        this.formType === 'standalone' ? m('td', form.allow_modification() ? icon('fas fa-check') : null) : null,
                        m('td', submissionCount(form)),
                        m('td', [
                            LinkButton.component({
                                className: 'Button',
                                href: formulaireFormEdit(form),
                            }, app.translator.trans(translationPrefix + 'edit')),
                            ' ',
                            LinkButton.component({
                                className: 'Button',
                                href: formulaireFormShow(form),
                            }, app.translator.trans(translationPrefix + 'show')),
                            ' ',
                            LinkButton.component({
                                className: 'Button',
                                href: formulaireSubmissionIndex(form),
                            }, app.translator.trans(translationPrefix + 'submissions')),
                        ]),
                    ]);
                })),
            ]),
            loading,
        ]);
    }
}
